import type { Worksheet } from "exceljs";
import type { DateTime } from "luxon";
import type { CensusPointInTimes } from "../../../utils/models";
import { censusPointInTimeCells } from "../util/censusPointInTimes";

/**
 * Cap on census readings per header line. Only stops a department with many configured reading
 * hours from pushing the header past the printable width; `totalColumns` is the real bound.
 */
const MAX_POINT_IN_TIME_CELLS_PER_ROW = 8;

/** 1-based Excel column indices — `mergeCells`/`getCell` take indices, never letters. */
const FIRST_COLUMN = 1;
const DATE_MERGE_END_COLUMN = 2;
const CENSUS_COLUMN = 3;
const HPPD_COLUMN = 5;
const NURSING_HOURS_MERGE_END_COLUMN = 3;

/** Below this width the spread-out stats layout would not fit; narrower sheets pack instead. */
const MIN_COLUMNS_FOR_SPREAD_STATS = HPPD_COLUMN;

/** Fixed rows of the header block. Row 2 is deliberately blank — spacing under the title. */
const TITLE_ROW = 1;
const STATS_FIRST_ROW = 3;

const TITLE_FONT_SIZE = 16;
const DATE_FONT_SIZE = 12;

/** Below this, column counts are degenerate; see the `// DEFAULT:` note at the call site. */
const MIN_SHEET_COLUMNS = 1;

interface RenderSheetHeaderArgs {
	worksheet: Worksheet;
	title: string;
	date: DateTime;
	census: number;
	hppd: number;
	nursingHours: number;
	hideHppdNursingHours: boolean;
	censusPointInTimes: CensusPointInTimes | undefined;
	/** The sheet's real column count — `worksheet.columns?.length`, set by the caller before rendering. */
	totalColumns: number;
}

interface RenderStatsRowsArgs {
	worksheet: Worksheet;
	row: number;
	lastColumn: number;
	date: DateTime;
	census: number;
	hppd: number;
	hideHppdNursingHours: boolean;
}

const setBoldCell = (worksheet: Worksheet, row: number, column: number, value: string, size?: number): void => {
	const cell = worksheet.getCell(row, column);
	cell.value = value;
	cell.font = size === undefined ? { bold: true } : { bold: true, size };
};

/**
 * Emit the date / census / HPPD line and return the first row after it. Two layouts: the original
 * merged one needs five columns, and narrower sheets pack the stats consecutively instead — HPPD
 * at column E of a four-column sheet is a populated cell past the real table (APP-406).
 */
const renderStatsRows = ({ worksheet, row, lastColumn, date, census, hppd, hideHppdNursingHours }: RenderStatsRowsArgs): number => {
	const dateText = `${date.toFormat("yyyy-MM-dd")} ${date.toFormat("cccc")}`;
	const censusText = `Census: ${census}`;
	const hppdText = `HPPD: ${hppd.toFixed(2)}`;

	if (lastColumn >= MIN_COLUMNS_FOR_SPREAD_STATS) {
		setBoldCell(worksheet, row, FIRST_COLUMN, dateText, DATE_FONT_SIZE);
		worksheet.mergeCells(row, FIRST_COLUMN, row, DATE_MERGE_END_COLUMN);
		setBoldCell(worksheet, row, CENSUS_COLUMN, censusText);
		if (!hideHppdNursingHours) setBoldCell(worksheet, row, HPPD_COLUMN, hppdText);
		return row + 1;
	}

	const stats = hideHppdNursingHours ? [dateText, censusText] : [dateText, censusText, hppdText];
	stats.forEach((text, index) => {
		// Keep the date's larger font wherever it lands, so both layouts read the same.
		setBoldCell(worksheet, row + Math.floor(index / lastColumn), (index % lastColumn) + 1, text, index === 0 ? DATE_FONT_SIZE : undefined);
	});
	return row + Math.ceil(stats.length / lastColumn);
};

/**
 * Emit the sheet's header block (mapping §9.6) and return the row where data should begin. Row 2 is
 * intentionally blank — spacing under the title.
 *
 * Every address derives from `totalColumns`, never a notional six-column sheet: a cell past the real
 * table widens the used range, which both Excel's fit-to-page scaling and the PDF path render as
 * phantom columns, and a six-column merge under-spans a 12+ column pivot sheet (APP-406).
 */
export const renderSheetHeader = ({
	worksheet,
	title,
	date,
	census,
	hppd,
	nursingHours,
	hideHppdNursingHours,
	censusPointInTimes,
	totalColumns,
}: RenderSheetHeaderArgs): number => {
	// DEFAULT: every column disabled yields `totalColumns === 0`, and exceljs has no column 0, so
	// every address below would be invalid and take the whole printout down. The empty body already
	// tells the user the sheet has no columns.
	const lastColumn = Math.max(MIN_SHEET_COLUMNS, totalColumns);

	const titleCell = worksheet.getCell(TITLE_ROW, FIRST_COLUMN);
	titleCell.value = title;
	titleCell.font = { bold: true, size: TITLE_FONT_SIZE };
	// Numeric merge, never a column letter: the `String.fromCharCode` idiom silently yields
	// non-letters past column 26, and pivot sheets go wider. A 1x1 merge is a corrupt range in the
	// produced .xlsx, so a one-column sheet gets no merge at all.
	if (lastColumn > FIRST_COLUMN) worksheet.mergeCells(TITLE_ROW, FIRST_COLUMN, TITLE_ROW, lastColumn);
	titleCell.alignment = { horizontal: "left" };

	let currentRow = renderStatsRows({ worksheet, row: STATS_FIRST_ROW, lastColumn, date, census, hppd, hideHppdNursingHours });

	if (!hideHppdNursingHours) {
		setBoldCell(worksheet, currentRow, FIRST_COLUMN, `Nursing Hours: ${nursingHours.toFixed(1)}`);
		const nursingHoursMergeEnd = Math.min(NURSING_HOURS_MERGE_END_COLUMN, lastColumn);
		if (nursingHoursMergeEnd > FIRST_COLUMN) worksheet.mergeCells(currentRow, FIRST_COLUMN, currentRow, nursingHoursMergeEnd);
	}

	// Advance whether or not the row was written: hiding the stats leaves it blank rather than
	// pulling the whole sheet up a line.
	currentRow += 1;

	// Addressed by index, not by a letter from a fixed list: reading hours are department config
	// since DAT-717, so a 5th reading resolved to the address "undefined<row>", which exceljs rejects
	// — taking down the whole printout. Absent readings leave no gap, so the row can be shorter.
	const cellsPerRow = Math.min(MAX_POINT_IN_TIME_CELLS_PER_ROW, lastColumn);
	const pointInTimeCells = censusPointInTimeCells(censusPointInTimes);
	pointInTimeCells.forEach((text, index) => {
		setBoldCell(worksheet, currentRow + Math.floor(index / cellsPerRow), (index % cellsPerRow) + 1, text);
	});

	return currentRow + Math.ceil(pointInTimeCells.length / cellsPerRow);
};
